
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';

export default function Products() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const generatorsRef = useRef(null);
  const separatorsRef = useRef(null);
  const compressorsRef = useRef(null);
  const sparesRef = useRef(null);

  const generatorsInView = useInView(generatorsRef, { once: true, margin: "-100px" });
  const separatorsInView = useInView(separatorsRef, { once: true, margin: "-100px" });
  const compressorsInView = useInView(compressorsRef, { once: true, margin: "-100px" });
  const sparesInView = useInView(sparesRef, { once: true, margin: "-100px" });

  const handleInquireClick = () => {
    setShowContactModal(true);
  };

  const closeModal = () => {
    setShowContactModal(false);
  };

  const handleProductClick = (product: any, type: string) => {
    const productWithType = { ...product, productType: type, id: `${type}-${product.name.replace(/\s+/g, '-').toLowerCase()}` };
    setSelectedProduct(productWithType);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
  };

  // Cool container animation with wave effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  // Enhanced card animation with 3D effects
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      rotateX: -15,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  // Magnetic hover effect
  const cardHoverVariants = {
    hover: {
      y: -15,
      rotateY: 5,
      rotateX: 5,
      scale: 1.05,
      boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    }
  };

  // Title animation with typewriter effect
  const titleVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  // Button animation with pulse effect
  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 30px rgba(59, 130, 246, 0.4)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: {
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 600,
        damping: 15
      }
    }
  };

  const generators = [
    {
      name: 'Wärtsilä 18V32LN',
      power: '9000kW',
      fuel: 'HFO (Heavy Fuel Oil) / Diesel',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/0796e3c74f468168e93ceff1afae352f.jpeg',
      features: ['Medium speed marine engine', 'Low NOx emissions', 'High fuel efficiency', 'Proven reliability'],
      condition: 'Excellent'
    },
    {
      name: 'MAN B&W 18V 32/40',
      power: '8640kW',
      fuel: 'HFO (Heavy Fuel Oil) / Diesel',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/dca7a0322f57c59a96f059fb6965baa5.jpeg',
      features: ['Medium speed marine engine', 'High power density', 'Fuel flexible operation', 'Robust construction', 'Low maintenance'],
      condition: 'Excellent'
    },
    {
      name: 'Wärtsilä W20V34SG',
      power: '8500kW',
      fuel: 'Diesel / HFO (Heavy Fuel Oil)',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/c0b4aac8b23ce79ab7a6862730e30240.webp',
      features: ['Medium speed engine', 'HFO compatible', 'Low emissions', 'Modular design'],
      condition: 'Excellent'
    },
    {
      name: 'Daihatsu 6DK-28',
      power: '1200kW',
      fuel: 'HFO (Heavy Fuel Oil) / Diesel',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/7c83d1773a1e384407d0ffe568656f90.jpeg',
      features: ['Medium speed operation', 'HFO optimized', 'Robust construction', 'Marine grade'],
      condition: 'Very Good'
    },
    {
      name: 'MAN 8L48/60CR',
      power: '2400kW',
      fuel: 'Diesel / HFO (Heavy Fuel Oil)',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/00fe32e7e07f1f13aef897a90b3c707c.webp',
      features: ['Medium speed design', 'German engineering', 'HFO capability', 'Advanced technology'],
      condition: 'Very Good'
    },
    {
      name: 'Mitsubishi S6R2-MPTK',
      power: '1500kW',
      fuel: 'Diesel / HFO (Heavy Fuel Oil)',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/ad1eac50ab0c65bdeb1a71df68eee0fc.jpeg',
      features: ['Medium speed turbocharged', 'HFO compatible', 'Low maintenance', 'Proven reliability'],
      condition: 'Excellent'
    },
    {
      name: 'Yanmar 6EY26W',
      power: '1800kW',
      fuel: 'HFO (Heavy Fuel Oil) / Diesel',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/8308ddf30b6f114ea2b11f00c910d497.jpeg',
      features: ['Medium speed marine certified', 'HFO optimized', 'Durable construction', 'Easy maintenance'],
      condition: 'Good'
    },
    {
      name: 'Caterpillar 3516B',
      power: '2000kW',
      fuel: 'Diesel / HFO (Heavy Fuel Oil)',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/083c6fa0dae15dbc59d9e498b0305eb7.jpeg',
      features: ['Medium speed heavy-duty', 'HFO capable', 'Global support', 'Field proven'],
      condition: 'Very Good'
    },
    {
      name: 'Niigata 6L28HX',
      power: '900kW',
      fuel: 'Diesel / HFO (Heavy Fuel Oil)',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/0027003bccd785b76da6e9b60c115645.webp',
      features: ['Medium speed marine optimized', 'HFO compatible', 'Compact design', 'Reliable performance'],
      condition: 'Excellent'
    },
    {
      name: 'Sulzer 6RTA58T',
      power: '15000kW',
      fuel: 'HFO (Heavy Fuel Oil) / Diesel',
      image: 'https://public.readdy.ai/ai/img_res/13c7bd6882c80a3deb7161dc63841eb9.jpg',
      features: ['Medium speed ultra-high power', 'HFO optimized', 'Marine certified', 'Advanced control'],
      condition: 'Good'
    }
  ];

  const separators = [
    {
      name: 'Alfa Laval MAPX 207',
      type: 'HFO Separator',
      capacity: '4000 L/h',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/62acd8c8652d0e52cab017a343c7643f.png',
      features: ['Self-cleaning', 'High efficiency', 'Marine certified', 'Low maintenance'],
      condition: 'Excellent'
    },
    {
      name: 'Mitsubishi SJ-3000',
      type: 'HFO Separator',
      capacity: '3000 L/h',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/a811e4dcdbd1fea922935386c44604cd.jpeg',
      features: ['Automatic operation', 'Energy efficient', 'Compact design', 'Reliable performance'],
      condition: 'Very Good'
    },
    {
      name: 'Alfa Laval MOPX 205',
      type: 'Lube Oil Separator',
      capacity: '2500 L/h',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/31a23e9e094115310045e56a7def2131.png',
      features: ['Precision separation', 'Self-cleaning bowl', 'Marine approved', 'Easy operation'],
      condition: 'Excellent'
    },
    {
      name: 'Westfalia OSE 20',
      type: 'Lube Oil Separator',
      capacity: '2000 L/h',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/fd558703445418bd07cb4028923b59f1.png',
      features: ['German engineering', 'High separation efficiency', 'Durable construction', 'Low operating cost'],
      condition: 'Very Good'
    }
  ];

  const compressors = [
    {
      name: 'Atlas Copco GA 75',
      type: 'Air Compressor',
      capacity: '75 kW',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/710ca48210cb9c43e662f7e0a61d48b5.jpeg',
      features: ['Energy efficient', 'Low noise', 'Reliable operation', 'Easy maintenance'],
      condition: 'Excellent'
    },
    {
      name: 'Ingersoll Rand SSR-EP100',
      type: 'Air Compressor',
      capacity: '100 HP',
      image: 'https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/04a4a1e397f1c08673c6b1521592059a.jpeg',
      features: ['High efficiency', 'Durable design', 'Advanced controls', 'Field proven'],
      condition: 'Very Good'
    }
  ];

  const spares = [
    {
      name: 'Generator Spare Parts',
      category: 'All Brands',
      image: 'https://readdy.ai/api/search-image?query=Generator%20spare%20parts%20collection%20including%20pistons%20rings%20cylinder%20heads%20fuel%20injectors%20in%20organized%20professional%20workshop%2C%20blue%20and%20gray%20industrial%20environment%2C%20technical%20components%20display&width=800&height=600&seq=generator-spares&orientation=portrait',
      items: ['Pistons & Rings', 'Cylinder Heads', 'Fuel Injectors', 'Filters & Gaskets', 'Bearings', 'Control Panels']
    },
    {
      name: 'Separator Spare Parts',
      category: 'HFO & Lube Oil',
      image: 'https://readdy.ai/api/search-image?query=Separator%20spare%20parts%20collection%20including%20bowls%20disc%20stacks%20seals%20in%20professional%20workshop%20environment%2C%20blue%20and%20gray%20industrial%20setting%2C%20organized%20technical%20components&width=800&height=600&seq=separator-spares&orientation=portrait',
      items: ['Separator Bowls', 'Disc Stacks', 'Seals & O-rings', 'Drive Belts', 'Control Units', 'Sensors']
    },
    {
      name: 'Compressor Spare Parts',
      category: 'Air Compressors',
      image: 'https://readdy.ai/api/search-image?query=Air%20compressor%20spare%20parts%20collection%20including%20filters%20valves%20coolers%20in%20modern%20industrial%20workshop%2C%20professional%20blue%20and%20gray%20setting%2C%20organized%20technical%20components%20display&width=800&height=600&seq=compressor-spares&orientation=portrait',
      items: ['Air Filters', 'Oil Filters', 'Separator Elements', 'Valves', 'Coolers', 'Control Modules']
    }
  ];

  return (
    <>
      <section id="products" className="py-20 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Generators Section */}
          <div className="mb-20" ref={generatorsRef}>
            <motion.div 
              className="text-center mb-16"
              variants={titleVariants}
              initial="hidden"
              animate={generatorsInView ? "visible" : "hidden"}
            >
              <motion.h2 
                className="text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={generatorsInView ? { 
                  opacity: 1, 
                  scale: 1,
                  transition: {
                    type: "spring",
                    damping: 10,
                    stiffness: 100,
                    duration: 0.8
                  }
                } : { opacity: 0, scale: 0.5 }}
              >
                Medium Speed Used Industrial Generators
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={generatorsInView ? { 
                  opacity: 1, 
                  y: 0,
                  transition: { delay: 0.2, duration: 0.6 }
                } : { opacity: 0, y: 30 }}
              >
                Explore our selection of high-quality used medium speed industrial diesel generators running on 
                Diesel and HFO (Heavy Fuel Oil) from world-leading manufacturers. All units are thoroughly 
                inspected and tested to ensure reliable performance for your power generation needs.
              </motion.p>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-product-shop
              variants={containerVariants}
              initial="hidden"
              animate={generatorsInView ? "visible" : "hidden"}
            >
              {generators.map((product, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer perspective-1000"
                  onClick={() => handleProductClick(product, 'generator')}
                  variants={cardVariants}
                  whileHover={cardHoverVariants.hover}
                  whileTap={{ scale: 0.98 }}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <motion.img 
                      src={product.image}
                      alt={`${product.name} medium speed industrial diesel HFO generator`}
                      className="w-full h-full object-cover object-top"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.div 
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: index * 0.1 + 0.5, type: "spring", stiffness: 200 }}
                    >
                      {product.power}
                    </motion.div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                    
                    <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-3">
                      <i className="ri-gas-station-line mr-2 flex-shrink-0"></i>
                      <span className="truncate">{product.fuel}</span>
                    </div>

                    <div className="mb-3 sm:mb-4">
                      <div className="flex items-center text-xs sm:text-sm mb-2">
                        <i className="ri-checkbox-circle-line text-green-600 mr-2 flex-shrink-0"></i>
                        <span className="font-semibold text-gray-900">Condition: {product.condition}</span>
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-center text-xs sm:text-sm text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + idx * 0.1 + 0.8 }}
                        >
                          <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0"></i>
                          <span className="line-clamp-1">{feature}</span>
                        </motion.div>
                      ))}
                      {product.features.length > 3 && (
                        <div className="text-xs text-gray-500 pl-5">
                          +{product.features.length - 3} more features
                        </div>
                      )}
                    </div>

                    <motion.button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInquireClick();
                      }}
                      className="w-full bg-accent-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Inquire Now
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Separators Section */}
          <div className="mb-20" ref={separatorsRef}>
            <motion.div 
              className="text-center mb-16"
              variants={titleVariants}
              initial="hidden"
              animate={separatorsInView ? "visible" : "hidden"}
            >
              <motion.h2 
                className="text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: -50, rotateX: 90 }}
                animate={separatorsInView ? { 
                  opacity: 1, 
                  y: 0,
                  rotateX: 0,
                  transition: {
                    type: "spring",
                    damping: 12,
                    stiffness: 100,
                    duration: 0.8
                  }
                } : { opacity: 0, y: -50, rotateX: 90 }}
              >
                HFO & Lube Oil Separators
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={separatorsInView ? { 
                  opacity: 1, 
                  y: 0,
                  transition: { delay: 0.2, duration: 0.6 }
                } : { opacity: 0, y: 30 }}
              >
                Premium quality HFO separators and lube oil separators from leading manufacturers. 
                We supply all types of separators and provide comprehensive maintenance services to ensure 
                optimal performance and longevity of your equipment.
              </motion.p>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-product-shop
              variants={containerVariants}
              initial="hidden"
              animate={separatorsInView ? "visible" : "hidden"}
            >
              {separators.map((product, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer"
                  onClick={() => handleProductClick(product, 'separator')}
                  variants={cardVariants}
                  whileHover={cardHoverVariants.hover}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <motion.img 
                      src={product.image}
                      alt={`${product.name} ${product.type}`}
                      className="w-full h-full object-cover object-top"
                      whileHover={{ scale: 1.1, rotate: 2 }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.div 
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-green-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold"
                      initial={{ scale: 0, rotate: 180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: index * 0.1 + 0.5, type: "spring", stiffness: 200 }}
                    >
                      {product.capacity}
                    </motion.div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <div className="mb-2">
                      <motion.span 
                        className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 + 0.6 }}
                      >
                        {product.type}
                      </motion.span>
                    </div>
                    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-3 line-clamp-2">{product.name}</h3>

                    <div className="mb-3 sm:mb-4">
                      <div className="flex items-center text-xs sm:text-sm mb-2">
                        <i className="ri-checkbox-circle-line text-green-600 mr-2 flex-shrink-0"></i>
                        <span className="font-semibold text-gray-900">Condition: {product.condition}</span>
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-center text-xs sm:text-sm text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + idx * 0.1 + 0.8 }}
                        >
                          <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0"></i>
                          <span className="line-clamp-1">{feature}</span>
                        </motion.div>
                      ))}
                      {product.features.length > 3 && (
                        <div className="text-xs text-gray-500 pl-5">
                          +{product.features.length - 3} more features
                        </div>
                      )}
                    </div>

                    <motion.button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInquireClick();
                      }}
                      className="w-full bg-accent-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Inquire Now
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Air Compressors Section */}
          <div className="mb-20" ref={compressorsRef}>
            <motion.div 
              className="text-center mb-16"
              variants={titleVariants}
              initial="hidden"
              animate={compressorsInView ? "visible" : "hidden"}
            >
              <motion.h2 
                className="text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: -50, rotateX: 90 }}
                animate={compressorsInView ? { 
                  opacity: 1, 
                  y: 0,
                  rotateX: 0,
                  transition: {
                    type: "spring",
                    damping: 12,
                    stiffness: 100,
                    duration: 0.8
                  }
                } : { opacity: 0, y: -50, rotateX: 90 }}
              >
                Industrial Air Compressors
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={compressorsInView ? { 
                  opacity: 1, 
                  y: 0,
                  transition: { delay: 0.2, duration: 0.6 }
                } : { opacity: 0, y: 30 }}
              >
                High-performance industrial air compressors for all your compressed air needs. 
                We supply quality air compressors and provide complete maintenance services to keep 
                your operations running smoothly and efficiently.
              </motion.p>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-product-shop
              variants={containerVariants}
              initial="hidden"
              animate={compressorsInView ? "visible" : "hidden"}
            >
              {compressors.map((product, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer"
                  onClick={() => handleProductClick(product, 'compressor')}
                  variants={cardVariants}
                  whileHover={cardHoverVariants.hover}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <motion.img 
                      src={product.image}
                      alt={`${product.name} ${product.type}`}
                      className="w-full h-full object-cover object-top"
                      whileHover={{ scale: 1.15, rotateZ: -2 }}
                      transition={{ duration: 0.4 }}
                    />
                    <motion.div 
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-orange-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: index * 0.2 + 0.5, type: "spring", stiffness: 200 }}
                    >
                      {product.capacity}
                    </motion.div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <div className="mb-2">
                      <motion.span 
                        className="inline-block bg-orange-100 text-orange-800 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.2 + 0.6 }}
                      >
                        {product.type}
                      </motion.span>
                    </div>
                    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-3 line-clamp-2">{product.name}</h3>

                    <div className="mb-3 sm:mb-4">
                      <div className="flex items-center text-xs sm:text-sm mb-2">
                        <i className="ri-checkbox-circle-line text-green-600 mr-2 flex-shrink-0"></i>
                        <span className="font-semibold text-gray-900">Condition: {product.condition}</span>
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-center text-xs sm:text-sm text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.2 + idx * 0.1 + 0.8 }}
                        >
                          <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0"></i>
                          <span className="line-clamp-1">{feature}</span>
                        </motion.div>
                      ))}
                      {product.features.length > 3 && (
                        <div className="text-xs text-gray-500 pl-5">
                          +{product.features.length - 3} more features
                        </div>
                      )}
                    </div>

                    <motion.button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInquireClick();
                      }}
                      className="w-full bg-accent-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Inquire Now
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Spare Parts Section */}
          <div ref={sparesRef}>
            <motion.div 
              className="text-center mb-16"
              variants={titleVariants}
              initial="hidden"
              animate={sparesInView ? "visible" : "hidden"}
            >
              <motion.h2 
                className="text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: -50, rotateX: 90 }}
                animate={sparesInView ? { 
                  opacity: 1, 
                  y: 0,
                  rotateX: 0,
                  transition: {
                    type: "spring",
                    damping: 12,
                    stiffness: 100,
                    duration: 0.8
                  }
                } : { opacity: 0, y: -50, rotateX: 90 }}
              >
                Spare Parts Supply
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={sparesInView ? { 
                  opacity: 1, 
                  y: 0,
                  transition: { delay: 0.2, duration: 0.6 }
                } : { opacity: 0, y: 30 }}
              >
                Comprehensive inventory of genuine and OEM spare parts for generators, separators, and air compressors. 
                We supply all types of spare parts to keep your equipment running at peak performance with minimal downtime.
              </motion.p>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              data-product-shop
              variants={containerVariants}
              initial="hidden"
              animate={sparesInView ? "visible" : "hidden"}
            >
              {spares.map((product, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer"
                  onClick={() => handleProductClick(product, 'spare')}
                  variants={cardVariants}
                  whileHover={cardHoverVariants.hover}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <motion.img 
                      src={product.image}
                      alt={`${product.name} - ${product.category}`}
                      className="w-full h-full object-cover object-top"
                      whileHover={{ scale: 1.1, rotateY: 5 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <div className="mb-2">
                      <motion.span 
                        className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.15 + 0.6 }}
                      >
                        {product.category}
                      </motion.span>
                    </div>
                    <motion.h3 
                      className="text-base sm:text-xl font-bold text-gray-900 mb-4 line-clamp-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      {product.name}
                    </motion.h3>

                    <div className="space-y-1 sm:space-y-2 mb-4 sm:mb-6">
                      {product.items.slice(0, 4).map((item, idx) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-center text-xs sm:text-sm text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.15 + idx * 0.1 + 0.8 }}
                        >
                          <i className="ri-checkbox-line text-green-600 mr-2 flex-shrink-0"></i>
                          <span className="line-clamp-1">{item}</span>
                        </motion.div>
                      ))}
                      {product.items.length > 4 && (
                        <div className="text-xs text-gray-500 pl-5">
                          +{product.items.length - 4} more items
                        </div>
                      )}
                    </div>

                    <motion.button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInquireClick();
                      }}
                      className="w-full bg-accent-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <i className="ri-shopping-cart-line mr-2"></i>
                      Request Quote
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0, y: 50 }}
            animate={sparesInView ? { 
              opacity: 1, 
              y: 0,
              transition: { delay: 0.5, duration: 0.8 }
            } : { opacity: 0, y: 50 }}
          >
            <p className="text-gray-600 mb-6">
              Looking for specific equipment or spare parts? Contact us for our complete inventory and maintenance services.
            </p>
            <motion.a 
              href="#contact"
              className="inline-block bg-accent-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 15px 35px rgba(255, 102, 0, 0.3)",
                y: -3
              }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us for Complete Inventory
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Product Details Modal */}
      {showProductModal && selectedProduct && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeProductModal}
        >
          <motion.div 
            className="bg-white rounded-xl max-w-6xl w-full my-2 sm:my-8 relative min-h-fit"
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            exit={{ opacity: 0, scale: 0.8, rotateY: 15 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.button 
              onClick={closeProductModal}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 z-20 bg-white rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 cursor-pointer shadow-lg border border-gray-200"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="ri-close-line text-lg sm:text-2xl"></i>
            </motion.button>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6">
              {/* Image Section */}
              <motion.div 
                className="relative order-1"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <img 
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96 xl:h-[500px] object-cover rounded-lg shadow-lg"
                />
              </motion.div>

              {/* Details Section */}
              <motion.div 
                className="flex flex-col order-2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="mb-2 sm:mb-3">
                  {selectedProduct.type && (
                    <motion.span 
                      className="inline-block bg-blue-100 text-blue-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full mr-2 mb-2"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {selectedProduct.type}
                    </motion.span>
                  )}
                  {selectedProduct.category && (
                    <motion.span 
                      className="inline-block bg-purple-100 text-purple-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {selectedProduct.category}
                    </motion.span>
                  )}
                </div>

                <motion.h2 
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 pr-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {selectedProduct.name}
                </motion.h2>

                {/* Specifications */}
                <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4 md:mb-6">
                  {selectedProduct.fuel && (
                    <motion.div 
                      className="flex items-center text-gray-700"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <i className="ri-gas-station-fill text-blue-600 text-base sm:text-lg md:text-xl mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <div className="text-xs text-gray-500">Fuel Type</div>
                        <div className="font-semibold text-xs sm:text-sm md:text-base">{selectedProduct.fuel}</div>
                      </div>
                    </motion.div>
                  )}

                  {selectedProduct.condition && (
                    <motion.div 
                      className="flex items-center text-gray-700"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      <i className="ri-checkbox-circle-fill text-green-600 text-base sm:text-lg md:text-xl mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <div className="text-xs text-gray-500">Condition</div>
                        <div className="font-semibold text-xs sm:text-sm md:text-base">{selectedProduct.condition}</div>
                      </div>
                    </motion.div>
                  )}

                  {selectedProduct.productType === 'generator' && (
                    <motion.div 
                      className="flex items-center text-gray-700"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                    >
                      <i className="ri-flashlight-fill text-orange-600 text-base sm:text-lg md:text-xl mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <div className="text-xs text-gray-500">Type</div>
                        <div className="font-semibold text-xs sm:text-sm md:text-base">Medium Speed Industrial Generator</div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Features */}
                {selectedProduct.features && (
                  <motion.div 
                    className="mb-3 sm:mb-4 md:mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-2 sm:mb-3">Key Features</h3>
                    <div className="space-y-1 sm:space-y-2 max-h-24 sm:max-h-32 md:max-h-40 overflow-y-auto">
                      {selectedProduct.features.map((feature: string, idx: number) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-start"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1 + idx * 0.1 }}
                        >
                          <i className="ri-check-line text-blue-600 mr-2 mt-0.5 flex-shrink-0 text-sm"></i>
                          <span className="text-xs sm:text-sm md:text-base text-gray-700">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Items (for spare parts) */}
                {selectedProduct.items && (
                  <motion.div 
                    className="mb-3 sm:mb-4 md:mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-2 sm:mb-3">Available Parts</h3>
                    <div className="grid grid-cols-1 gap-1 sm:gap-2 max-h-24 sm:max-h-32 md:max-h-40 overflow-y-auto">
                      {selectedProduct.items.map((item: string, idx: number) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-start"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1 + idx * 0.1 }}
                        >
                          <i className="ri-checkbox-line text-green-600 mr-2 mt-0.5 flex-shrink-0 text-sm"></i>
                          <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Contact Button */}
                <motion.div 
                  className="mt-auto pt-3 sm:pt-4 border-t border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  <motion.button 
                    onClick={(e) => {
                      e.stopPropagation();
                      closeProductModal();
                      handleInquireClick();
                    }}
                    className="w-full bg-accent-600 text-white py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm md:text-lg font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 15px 35px rgba(255, 102, 0, 0.3)"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Contact Us for This Product
                  </motion.button>
                  <p className="text-center text-xs text-gray-500 mt-2 sm:mt-3">
                    Get pricing and availability details
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Contact Options Modal */}
      {showContactModal && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="bg-white rounded-xl max-w-md w-full p-6 relative"
            initial={{ opacity: 0, scale: 0.7, rotateX: -15 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, scale: 0.7, rotateX: 15 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            <motion.button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="ri-close-line text-2xl"></i>
            </motion.button>
            
            <motion.div 
              className="text-center mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Us</h3>
              <p className="text-gray-600">Choose your preferred contact method</p>
            </motion.div>

            <div className="space-y-4">
              <motion.a 
                href="tel:+919327380066"
                className="flex items-center w-full bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
                onClick={closeModal}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Call Now</div>
                  <div className="text-sm opacity-90">+91 9327380066</div>
                </div>
              </motion.a>

              <motion.a 
                href="https://wa.me/919327380066"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center w-full bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition-colors cursor-pointer"
                onClick={closeModal}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-whatsapp-line text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="font-semibold">WhatsApp</div>
                  <div className="text-sm opacity-90">+91 9327380066</div>
                </div>
              </motion.a>

              <motion.a 
                href="mailto:zaidenterprise0066@gmail.com"
                className="flex items-center w-full bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                onClick={closeModal}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Email</div>
                  <div className="text-sm opacity-90">zaidenterprise0066@gmail.com</div>
                </div>
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
